import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate
} from 'react-router-dom';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot
} from 'firebase/firestore';
import { auth, db } from './firebase';

// Global Overlays
import IncomingCallListener from './pages/IncomingCallListener';

// Pages
import Splash from './pages/Splash';
import Login from './pages/Login';
import PrivacyPolicy from './pages/Privacy&Policy';
import Register from './pages/Register';
import TermsConditions from './pages/Terms&Conditions';
import OtpVerification from './pages/OtpVerification';
import Dashboard from './pages/Dashboard';
import ChatScreen from './pages/ChatScreen';
import GroupScreen from './pages/GroupScreen';
import CallScreen from './pages/CallScreen';
import GroupCall from './pages/GroupCall';
import CallLog from './pages/CallLog';
import CommunityScreen from './pages/CommunityScreen';
import BroadcastScreen from './pages/BroadcastScreen';
import WorkflowScreen from './pages/WorkflowScreen';
import Profile from './pages/Profile';
import ProfileInfo from './pages/ProfileInfo';
import Settings from './pages/Settings';
import Contract from './pages/Contract';
import Channel from './pages/Channel';
import ChangePassword from './pages/ChangePassword';
import BlockList from './pages/BlockList';
import NotFound from './pages/NotFound';
import Editor from './pages/Editor';
import StoryMaker from './pages/StoryMaker';
import ApiPortal from './pages/ApiPortal';
import PageTransition from './components/PageTransition';
import { ThemeProvider } from './components/AppThemes';
import { runChatCacheMigration } from './utils/chatCache';

const CACHE_LIMIT = 50;

let setAppAlert = null;
let setAppConfirm = null;
let setAppPrompt = null;

function showAppAlert(message) {
  if (setAppAlert) {
    setAppAlert({
      open: true,
      message: String(message ?? '')
    });
  }
}

function requestAppConfirm(message) {
  if (!setAppConfirm) return Promise.resolve(false);
  return new Promise(resolve => {
    setAppConfirm({
      open: true,
      message: String(message ?? ''),
      resolve
    });
  });
}

function requestAppPrompt(message, defaultValue = '') {
  if (!setAppPrompt) return Promise.resolve(null);
  return new Promise(resolve => {
    setAppPrompt({
      open: true,
      message: String(message ?? ''),
      value: String(defaultValue ?? ''),
      resolve
    });
  });
}

function saveBackgroundChatCache(roomId, messages) {
  if (!roomId || !Array.isArray(messages)) return;

  try {
    const unreadIds = messages
      .filter(msg => msg?.senderId && msg.status !== 'seen')
      .filter(msg => msg.senderId !== (localStorage.getItem('nexora_user_uid') || auth.currentUser?.uid))
      .map(msg => msg.id)
      .slice(-CACHE_LIMIT);

    const payload = {
      messages: messages.length > CACHE_LIMIT ? messages.slice(-CACHE_LIMIT) : messages,
      unreadIds,
      unreadCount: unreadIds.length,
      savedAt: Date.now()
    };

    localStorage.setItem(`nexora_p2p_msgs_${roomId}`, JSON.stringify(payload));
  } catch (e) {
    // Storage can be full on some devices. The realtime app must continue working.
  }
}

/*
 * Background chat cache.
 *
 * This listener stays mounted at App level, so ChatScreen does not have
 * to wait for its first Firestore snapshot before showing messages.
 *
 * It NEVER marks messages as seen. ChatScreen keeps ownership of read/seen state.
 */
function BackgroundChatCache() {
  useEffect(() => {
    runChatCacheMigration();

    let unsubscribeChats = null;
    const messageUnsubscribers = new Map();

    const start = () => {
      const userUid =
        localStorage.getItem('nexora_user_uid') ||
        auth.currentUser?.uid;

      if (!userUid) return;

      const chatsQuery = query(
        collection(db, 'chats'),
        where('participants', 'array-contains', userUid)
      );

      unsubscribeChats = onSnapshot(
        chatsQuery,
        snapshot => {
          const activeRoomIds = new Set();

          snapshot.forEach(chatSnap => {
            const roomId = chatSnap.id;
            activeRoomIds.add(roomId);

            if (messageUnsubscribers.has(roomId)) return;

            const messagesRef = collection(db, 'chats', roomId, 'messages');
            const messagesQuery = query(
              messagesRef,
              orderBy('createdAt', 'desc'),
              limit(CACHE_LIMIT)
            );

            const unsubscribeMessages = onSnapshot(
              messagesQuery,
              messageSnapshot => {
                const messages = [];
                messageSnapshot.forEach(messageSnap => {
                  messages.push({
                    id: messageSnap.id,
                    ...messageSnap.data()
                  });
                });

                messages.reverse();
                saveBackgroundChatCache(roomId, messages);
              },
              () => {
                // Keep the rest of the app alive if a single room listener fails.
              }
            );

            messageUnsubscribers.set(roomId, unsubscribeMessages);
          });

          // Remove listeners for chats that are no longer in the user's chat list.
          messageUnsubscribers.forEach((unsubscribe, roomId) => {
            if (!activeRoomIds.has(roomId)) {
              unsubscribe();
              messageUnsubscribers.delete(roomId);
            }
          });
        },
        () => {
          // Firestore listener failure must not break navigation/UI.
        }
      );
    };

    // auth.currentUser can become available slightly after App mounts.
    start();

    const unsubscribeAuth = auth.onAuthStateChanged(user => {
      if (!user && unsubscribeChats) {
        unsubscribeChats();
        unsubscribeChats = null;
        messageUnsubscribers.forEach(unsubscribe => unsubscribe());
        messageUnsubscribers.clear();
      } else if (user && !unsubscribeChats) {
        start();
      }
    });

    return () => {
      if (unsubscribeChats) unsubscribeChats();
      messageUnsubscribers.forEach(unsubscribe => unsubscribe());
      messageUnsubscribers.clear();
      unsubscribeAuth();
    };
  }, []);

  return null;
}

function BackgroundCallOverlay() {
  const [activeCallData, setActiveCallData] = React.useState(null);
  const [refreshKey, setRefreshKey] = React.useState(0);

  useEffect(() => {
    const checkCall = () => {
      try {
        const stored = localStorage.getItem('nexora_active_call_session');
        if (stored) {
          const data = JSON.parse(stored);
          setActiveCallData(data);
        } else {
          setActiveCallData(null);
        }
      } catch (e) {}
    };

    checkCall();

    // Listen for changes to the active call session
    const interval = setInterval(checkCall, 500);
    window.addEventListener('storage', checkCall);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', checkCall);
    };
  }, []);

  if (!activeCallData) return null;

  // Render CallScreen but positioned outside the viewport so the floating bubble
  // remains visible and draggable while user navigates other pages
  return (
    <div style={{ 
      position: 'fixed', 
      width: '480px',
      height: '100dvh',
      top: -9999,
      left: 0,
      opacity: 1,
      pointerEvents: 'auto',
      zIndex: 999998
    }}>
      <CallScreen key={`bg-call-${activeCallData.callId || activeCallData.targetUid}-${refreshKey}`} />
    </div>
  );
}

function PersistentBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const [dashboardSelectionActive, setDashboardSelectionActive] = React.useState(false);

  const path = location.pathname;

  React.useEffect(() => {
    const handleSelectionChange = event => {
      setDashboardSelectionActive(Boolean(event.detail?.active));
    };
    window.addEventListener('nexoraDashboardSelection', handleSelectionChange);
    return () => window.removeEventListener('nexoraDashboardSelection', handleSelectionChange);
  }, []);

  // The bottom navigation intentionally remains mounted while moving between
  // Dashboard / Broadcast / Calls / Community. It does not participate in the
  // page slide, so it never flashes or moves with the page.
  const isMainShell =
    path === '/dashboard' ||
    path === '/broadcast' ||
    path === '/call-history' ||
    path === '/contract' ||
    path === '/community' ||
    path.startsWith('/community/');

  if (!isMainShell) return null;

  // Keep the footer mounted so it can animate smoothly instead of
  // disappearing/reappearing instantly. Hide it on Broadcast and on an
  // opened Community, while keeping it visible on the Community list.
  // পরিবর্তিত কোড:
const shouldHide =
  path.startsWith('/community/') ||
  dashboardSelectionActive;        // এটি ইভেন্টের মাধ্যমে হাইড করবে

  let active = 'Chats';
  if (path === '/broadcast') active = 'Broadcast';
  if (path === '/call-history') active = 'Calls';
  if (path === '/contract') active = 'Contract';
  if (path === '/community' || path.startsWith('/community/')) active = 'Communities';

  const go = nextPath => {
    if (path === nextPath) return;
    navigate(nextPath);
  };

  return (
    <div
      className={`nexora-persistent-bottom-nav${shouldHide ? ' nav-hidden' : ''}`}
      aria-hidden={shouldHide}
    >
      <button
        type="button"
        onClick={() => go('/dashboard')}
        className={`nexora-nav-item ${active === 'Chats' ? 'active' : ''}`}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
        <span>Chats</span>
      </button>

      <button
        type="button"
        onClick={() => go('/broadcast')}
        className={`nexora-nav-item ${active === 'Broadcast' ? 'active' : ''}`}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4 11a9 9 0 0 1 9 9"/>
          <path d="M4 4a16 16 0 0 1 16 16"/>
          <circle cx="5" cy="19" r="1"/>
        </svg>
        <span>Broadcast</span>
      </button>

      <button
        type="button"
        onClick={() => go('/contract')}
        className={`nexora-nav-item ${active === 'Contract' ? 'active' : ''}`}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="4" width="18" height="16" rx="3" />
          <path d="M7 8h10M7 12h6M7 16h4" />
        </svg>
        <span>Contract</span>
      </button>

      <button
        type="button"
        onClick={() => go('/call-history')}
        className={`nexora-nav-item ${active === 'Calls' ? 'active' : ''}`}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 0 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 0 22 16.92z"/>
        </svg>
        <span>Calls</span>
      </button>

      <button
        type="button"
        onClick={() => go('/community')}
        className={`nexora-nav-item ${active === 'Communities' ? 'active' : ''}`}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
          <circle cx="9" cy="7" r="4"/>
          <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
          <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
        </svg>
        <span>Communities</span>
      </button>
    </div>
  );
}

function RootRedirectGate() {
  const navigate = useNavigate();

  useEffect(() => {
    const storedUid =
      localStorage.getItem('nexora_user_uid') ||
      localStorage.getItem('uid') ||
      localStorage.getItem('currentUid') ||
      localStorage.getItem('userUid');

    if (storedUid || auth.currentUser) {
      navigate('/dashboard', { replace: true });
      return undefined;
    }

    const unsubscribe = auth.onAuthStateChanged(user => {
      if (user) {
        navigate('/dashboard', { replace: true });
      } else {
        navigate('/login', { replace: true });
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  return null;
}

function AppRoutes() {
  const renderRoutes = routeLocation => (
    <Routes location={routeLocation}>
      <Route path="/" element={<RootRedirectGate />} />
      <Route path="/login" element={<Login />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/register" element={<Register />} />
      <Route path="/terms-conditions" element={<TermsConditions />} />
      <Route path="/otp-verification" element={<OtpVerification />} />
      <Route path="/api" element={<ApiPortal />} />

      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/story-maker" element={<StoryMaker />} />

      <Route path="/chat" element={<ChatScreen />} />
      <Route path="/chat/:chatId" element={<ChatScreen />} />

      <Route path="/group" element={<GroupScreen />} />
      <Route path="/group/:groupId" element={<GroupScreen />} />

      <Route path="/call" element={<CallScreen />} />
      <Route path="/group-call" element={<GroupCall />} />
      <Route path="/call-history" element={<CallLog />} />

      <Route path="/community" element={<CommunityScreen />} />
      <Route path="/community/:communityId" element={<CommunityScreen />} />

      <Route path="/broadcast" element={<BroadcastScreen />} />
      <Route path="/workflow" element={<WorkflowScreen />} />

      <Route path="/profile" element={<Profile />} />
      <Route path="/profile-info/:uid" element={<ProfileInfo />} />

      <Route path="/settings" element={<Settings />} />
      <Route path="/editor" element={<Editor />} />
      <Route path="/contract" element={<Contract />} />
      <Route path="/channel" element={<Channel />} />
      <Route path="/change-password" element={<ChangePassword />} />
      <Route path="/block-list" element={<BlockList />} />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );

  return (
    <>
      <style>{`
        html, body, #root {
          margin: 0;
          padding: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          background: #000;
        }

        *, *::before, *::after {
          box-sizing: border-box;
        }

        .nexora-persistent-bottom-nav {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          height: 64px;
          padding-bottom: max(4px, env(safe-area-inset-bottom));
          background: rgba(255,255,255,.98);
          border-top: 1px solid #e5e7eb;
          box-shadow: 0 -4px 18px rgba(15,23,42,.06);
          display: flex;
          align-items: center;
          justify-content: space-around;
          z-index: 10000;
          transform: translate3d(0, 0, 0);
          -webkit-transform: translate3d(0, 0, 0);
          opacity: 1;
          visibility: visible;
          pointer-events: auto;
          transition:
            transform 280ms cubic-bezier(.22,1,.36,1),
            opacity 220ms ease,
            visibility 0s linear 0s;
          will-change: transform, opacity;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }

        .nexora-persistent-bottom-nav.nav-hidden {
          transform: translate3d(0, calc(100% + env(safe-area-inset-bottom)), 0);
          -webkit-transform: translate3d(0, calc(100% + env(safe-area-inset-bottom)), 0);
          opacity: 0;
          visibility: hidden;
          pointer-events: none;
          transition:
            transform 280ms cubic-bezier(.22,1,.36,1),
            opacity 180ms ease,
            visibility 0s linear 280ms;
        }

        .nexora-nav-item span {
  display: none; /* এটি টেক্সট নামগুলো মুছে ফেলবে */
}

.nexora-nav-item {
  height: 100%;
  min-width: 20%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center; /* আইকনগুলোকে মাঝখানে রাখবে */
  gap: 0; /* গ্যাপ শূন্য করে দিন যেহেতু টেক্সট নেই */
  border: 0;
  background: transparent;
  color: #94a3b8;
  -webkit-tap-highlight-color: transparent;
  cursor: pointer;
}

        .nexora-nav-item.active {
          color: #62b875;
          font-weight: 700;
        }

        .nexora-nav-item svg {
          flex: 0 0 auto;
        }

        @media (prefers-reduced-motion: reduce) {
          .nexora-persistent-bottom-nav {
            transition: none !important;
          }

          .nexora-persistent-bottom-nav.nav-hidden {
            transform: translate3d(0, calc(100% + env(safe-area-inset-bottom)), 0);
            -webkit-transform: translate3d(0, calc(100% + env(safe-area-inset-bottom)), 0);
          }
        }

        .nexora-alert-overlay {
          position: fixed;
          inset: 0;
          background: rgba(2, 6, 23, 0.72);
          z-index: 200000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
        }

        .nexora-alert-card {
          width: min(420px, 100%);
          background: linear-gradient(180deg, rgba(15,23,42,0.96), rgba(15,23,42,0.92));
          border: 1px solid rgba(148,163,184,0.28);
          border-radius: 20px;
          box-shadow: 0 28px 60px rgba(15, 23, 42, 0.45);
          padding: 20px 18px 16px;
          color: #f8fafc;
        }

        .nexora-alert-icon {
          width: 46px;
          height: 46px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 12px;
          font-size: 20px;
          background: rgba(96,165,250,0.12);
          color: #7dd3fc;
          border: 1px solid rgba(125, 211, 252, 0.24);
        }

        .nexora-alert-title {
          margin: 0 0 8px;
          text-align: center;
          font-size: 18px;
          font-weight: 800;
          letter-spacing: 0.02em;
        }

        .nexora-alert-message {
          margin: 0;
          color: #dbeafe;
          font-size: 15px;
          line-height: 1.6;
          text-align: center;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .nexora-alert-actions {
          margin-top: 18px;
          display: flex;
          justify-content: center;
        }

        .nexora-alert-button {
          border: none;
          border-radius: 12px;
          padding: 11px 18px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          background: linear-gradient(135deg, #60a5fa, #38bdf8);
          color: #082f49;
          box-shadow: 0 10px 24px rgba(96,165,250,0.32);
        }

        .nexora-confirm-overlay {
          position: fixed;
          inset: 0;
          background: rgba(2, 6, 23, 0.72);
          z-index: 200001;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
        }

        .nexora-confirm-card {
          width: min(420px, 100%);
          background: linear-gradient(180deg, #ffffff, #f8fafc);
          border: 1px solid rgba(148,163,184,0.3);
          border-radius: 22px;
          box-shadow: 0 28px 60px rgba(15,23,42,0.45);
          padding: 22px 18px 18px;
          color: #0f172a;
        }

        .nexora-confirm-icon {
          width: 46px;
          height: 46px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 12px;
          font-size: 22px;
          background: #fef3c7;
          color: #b45309;
          border: 1px solid #fde68a;
        }

        .nexora-confirm-title {
          margin: 0 0 8px;
          text-align: center;
          font-size: 18px;
          font-weight: 800;
        }

        .nexora-confirm-message {
          margin: 0;
          color: #475569;
          font-size: 15px;
          line-height: 1.6;
          text-align: center;
          white-space: pre-wrap;
          word-break: break-word;
        }

        .nexora-confirm-actions {
          display: flex;
          gap: 10px;
          margin-top: 20px;
        }

        .nexora-confirm-button {
          flex: 1;
          border: none;
          border-radius: 12px;
          padding: 11px 16px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
        }

        .nexora-confirm-cancel { background: #e2e8f0; color: #334155; }
        .nexora-confirm-accept { background: linear-gradient(135deg, #ef4444, #f97316); color: #fff; }

        .nexora-prompt-input {
          width: 100%;
          margin-top: 16px;
          padding: 12px 13px;
          border: 1px solid #cbd5e1;
          border-radius: 12px;
          outline: none;
          color: #0f172a;
          background: #fff;
          font-size: 15px;
        }

        .nexora-prompt-input:focus {
          border-color: #62b875;
          box-shadow: 0 0 0 3px rgba(98,184,117,.16);
        }
      `}</style>

      <PageTransition>{renderRoutes}</PageTransition>
      <PersistentBottomNav />
      <AppAlertHost />
      <AppConfirmHost />
      <AppPromptHost />
    </>
  );
}

function AppAlertHost() {
  const [alertState, setAlertState] = React.useState({ open: false, message: '' });

  React.useEffect(() => {
    setAppAlert = setAlertState;
    window.nexoraAlert = showAppAlert;

    const originalAlert = window.alert;

    window.alert = message => {
      showAppAlert(message);
      return undefined;
    };

    return () => {
      window.alert = originalAlert;
      setAppAlert = null;
      delete window.nexoraAlert;
    };
  }, []);

  const closeAlert = () => setAlertState({ open: false, message: '' });

  return (
    alertState.open && (
      <div
        className="nexora-alert-overlay"
        role="dialog"
        aria-modal="true"
        onClick={closeAlert}
      >
        <div className="nexora-alert-card" onClick={event => event.stopPropagation()}>
          <div className="nexora-alert-icon">i</div>
          <h3 className="nexora-alert-title">Notice</h3>
          <p className="nexora-alert-message">{alertState.message}</p>
          <div className="nexora-alert-actions">
            <button
              type="button"
              className="nexora-alert-button"
              onClick={closeAlert}
            >
              OK
            </button>
          </div>
        </div>
      </div>
    )
  );
}

function AppConfirmHost() {
  const [confirmState, setConfirmState] = React.useState({
    open: false,
    message: '',
    resolve: null
  });

  React.useEffect(() => {
    setAppConfirm = setConfirmState;
    window.nexoraConfirm = requestAppConfirm;

    return () => {
      setAppConfirm = null;
      delete window.nexoraConfirm;
    };
  }, []);

  const closeConfirm = result => {
    const resolve = confirmState.resolve;
    setConfirmState({ open: false, message: '', resolve: null });
    if (resolve) resolve(result);
  };

  if (!confirmState.open) return null;

  return (
    <div className="nexora-confirm-overlay" role="dialog" aria-modal="true" onClick={() => closeConfirm(false)}>
      <div className="nexora-confirm-card" onClick={event => event.stopPropagation()}>
        <div className="nexora-confirm-icon">?</div>
        <h3 className="nexora-confirm-title">Please confirm</h3>
        <p className="nexora-confirm-message">{confirmState.message}</p>
        <div className="nexora-confirm-actions">
          <button type="button" className="nexora-confirm-button nexora-confirm-cancel" onClick={() => closeConfirm(false)}>
            Cancel
          </button>
          <button type="button" className="nexora-confirm-button nexora-confirm-accept" onClick={() => closeConfirm(true)}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

function AppPromptHost() {
  const [promptState, setPromptState] = React.useState({
    open: false,
    message: '',
    value: '',
    resolve: null
  });
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    setAppPrompt = setPromptState;
    window.nexoraPrompt = requestAppPrompt;

    return () => {
      setAppPrompt = null;
      delete window.nexoraPrompt;
    };
  }, []);

  React.useEffect(() => {
    if (promptState.open) {
      window.requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [promptState.open]);

  const closePrompt = result => {
    const resolve = promptState.resolve;
    setPromptState({ open: false, message: '', value: '', resolve: null });
    if (resolve) resolve(result);
  };

  const submitPrompt = event => {
    event.preventDefault();
    closePrompt(promptState.value);
  };

  if (!promptState.open) return null;

  return (
    <div className="nexora-confirm-overlay" role="dialog" aria-modal="true" onClick={() => closePrompt(null)}>
      <form className="nexora-confirm-card" onClick={event => event.stopPropagation()} onSubmit={submitPrompt}>
        <div className="nexora-confirm-icon">✎</div>
        <h3 className="nexora-confirm-title">Enter a value</h3>
        <p className="nexora-confirm-message">{promptState.message}</p>
        <input
          ref={inputRef}
          className="nexora-prompt-input"
          value={promptState.value}
          onChange={event => setPromptState(previous => ({ ...previous, value: event.target.value }))}
          aria-label={promptState.message}
        />
        <div className="nexora-confirm-actions">
          <button type="button" className="nexora-confirm-button nexora-confirm-cancel" onClick={() => closePrompt(null)}>
            Cancel
          </button>
          <button type="submit" className="nexora-confirm-button nexora-confirm-accept">
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <Router>
      <style>{`
        *, *::before, *::after {
          user-select: none !important;
          -webkit-user-select: none !important;
          -webkit-touch-callout: none !important;
        }
      `}</style>
      <IncomingCallListener />
      <BackgroundChatCache />
      <BackgroundCallOverlay />
      <AppRoutes />
      </Router>
    </ThemeProvider>
  );
}

export default App;
